/**
 * Admin Layout Component
 * Header + Sidebar navigation for admin pages
 */
// @ts-nocheck - Temporary fix for react-icons type compatibility
import React from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Icon,
  Avatar,
  IconButton,
  Heading,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiHome,
  FiUsers,
  FiGift,
  FiActivity,
  FiShare2,
  FiBarChart2,
  FiArrowLeft,
} from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  icon: React.ElementType;
  label: string;
  path: string;
}

const navItems: NavItem[] = [
  { icon: FiHome, label: 'Dashboard', path: '/indra' },
  { icon: FiUsers, label: 'Users', path: '/indra/users' },
  { icon: FiGift, label: 'Invites', path: '/indra/invites' },
  { icon: FiActivity, label: 'Activity', path: '/indra/activity' },
  { icon: FiShare2, label: 'Shared Charts', path: '/indra/charts' },
  { icon: FiBarChart2, label: 'Stats', path: '/indra/stats' },
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const bgSidebar = useColorModeValue('gray.900', 'gray.900');
  const bgContent = useColorModeValue('gray.50', 'gray.800');
  const bgHeader = useColorModeValue('white', 'gray.900');

  const isActive = (path: string) => {
    if (path === '/indra') {
      return location.pathname === '/indra';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Flex h="100vh" overflow="hidden">
      {/* Sidebar */}
      <Box
        w="240px"
        bg={bgSidebar}
        color="white"
        flexShrink={0}
        display="flex"
        flexDirection="column"
      >
        {/* Sidebar Header */}
        <Box p={4} borderBottom="1px solid" borderColor="whiteAlpha.200">
          <HStack spacing={3}>
            <Box
              w={8}
              h={8}
              bg="blue.500"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontWeight="bold"
              fontSize="sm"
            >
              BH
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" fontSize="sm">Bhrigu Hora</Text>
              <Text fontSize="xs" color="whiteAlpha.600">Admin Panel</Text>
            </VStack>
          </HStack>
        </Box>

        {/* Navigation */}
        <VStack spacing={1} p={3} flex={1} align="stretch">
          {navItems.map((item) => (
            <Box
              key={item.path}
              px={3}
              py={2}
              borderRadius="md"
              cursor="pointer"
              bg={isActive(item.path) ? 'whiteAlpha.200' : 'transparent'}
              _hover={{ bg: 'whiteAlpha.100' }}
              onClick={() => navigate(item.path)}
              transition="all 0.2s"
            >
              <HStack spacing={3}>
                <Icon
                  as={item.icon}
                  boxSize={5}
                  color={isActive(item.path) ? 'blue.300' : 'whiteAlpha.700'}
                />
                <Text
                  fontSize="sm"
                  fontWeight={isActive(item.path) ? '600' : '400'}
                  color={isActive(item.path) ? 'white' : 'whiteAlpha.800'}
                >
                  {item.label}
                </Text>
              </HStack>
            </Box>
          ))}
        </VStack>

        {/* Sidebar Footer - User Info */}
        <Box p={4} borderTop="1px solid" borderColor="whiteAlpha.200">
          <HStack spacing={3}>
            <Avatar size="sm" name={user?.username} src={user?.avatar} />
            <VStack align="start" spacing={0} flex={1}>
              <Text fontSize="sm" fontWeight="500" noOfLines={1}>
                {user?.username}
              </Text>
              <Text fontSize="xs" color="whiteAlpha.600" noOfLines={1}>
                Administrator
              </Text>
            </VStack>
          </HStack>
        </Box>
      </Box>

      {/* Main Content Area */}
      <Flex flex={1} direction="column" overflow="hidden">
        {/* Header */}
        <Box
          bg={bgHeader}
          px={6}
          py={3}
          borderBottom="1px solid"
          borderColor="gray.200"
          flexShrink={0}
        >
          <HStack justify="space-between">
            <HStack spacing={4}>
              <IconButton
                aria-label="Back to app"
                icon={<Icon as={FiArrowLeft} />}
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
              />
              <Heading size="md" color="gray.700">
                {navItems.find(item => isActive(item.path))?.label || 'Admin'}
              </Heading>
            </HStack>
          </HStack>
        </Box>

        {/* Page Content */}
        <Box flex={1} overflow="auto" bg={bgContent}>
          {children}
        </Box>
      </Flex>
    </Flex>
  );
};

export default AdminLayout;
